import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { PageContainer } from '@/components/common/PageContainer'
import { AppButton } from '@/components/rfid/AppButton'
import { RfidAppHeader } from '@/components/rfid/RfidAppHeader'
import { ScanCaptureHint } from '@/components/scanner/ScanCaptureHint'
import {
  disconnect,
  subscribeTagReads,
} from '@/services/scannerService'
import { isScannerSessionActive, useRfidSessionStore } from '@/stores/rfidSessionStore'
import { formatDateTime } from '@/utils/cn'

const MAX_RECENT = 20

type TagRow = { key: string; tagId: string; at: string }

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="mb-1.5">
      <div className="text-[10px] font-extrabold uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="break-all font-mono text-xs font-semibold text-foreground">{value}</div>
    </div>
  )
}

export default function ScannerInfoPage() {
  const navigate = useNavigate()
  const scannerConnected = useRfidSessionStore((s) => s.scannerConnected)
  const scannerPending = useRfidSessionStore((s) => s.scannerPending)
  const scannerType = useRfidSessionStore((s) => s.scannerType)
  const deviceConnected = useRfidSessionStore((s) => s.deviceConnected)
  const lastScannerConnectAt = useRfidSessionStore((s) => s.lastScannerConnectAt)
  const [busy, setBusy] = useState(false)
  const [recentTags, setRecentTags] = useState<TagRow[]>([])

  const sessionActive = isScannerSessionActive({ scannerConnected, scannerPending })

  useEffect(() => {
    if (!sessionActive) return
    return subscribeTagReads((tagId) => {
      const row: TagRow = {
        key: `${tagId}-${Date.now()}`,
        tagId,
        at: new Date().toISOString(),
      }
      setRecentTags((prev) => [row, ...prev].slice(0, MAX_RECENT))
    })
  }, [sessionActive])

  const onDisconnect = async () => {
    setBusy(true)
    try {
      await disconnect()
      toast.message('Scanner cleared')
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Disconnect failed')
    } finally {
      setBusy(false)
    }
  }

  return (
    <PageContainer fluid className="!px-0 !py-0" stack={false}>
      <div className="rfid-web-frame">
        <RfidAppHeader
          title="Scanner info"
          subtitle="Connection status and live tag probe"
          onBack={() => navigate('/')}
          right={<ScanCaptureHint armed={sessionActive} showStatus={false} />}
        />

        <div className="rfid-wizard-body flex flex-1 flex-col gap-3.5 overflow-y-auto">
          <div
            className="flex items-center gap-3 rounded-xl border px-3.5 py-3.5"
            style={{
              backgroundColor: scannerConnected
                ? '#ECFDF5'
                : scannerPending
                  ? '#FFFBEB'
                  : '#FEF2F2',
              borderColor: scannerConnected
                ? '#86EFAC'
                : scannerPending
                  ? '#FDE68A'
                  : '#FECACA',
            }}
          >
            <div className="min-w-0 flex-1">
              <div
                className="text-sm font-black"
                style={{
                  color: scannerConnected
                    ? '#14532D'
                    : scannerPending
                      ? '#92400E'
                      : '#7F1D1D',
                }}
              >
                {scannerConnected
                  ? 'Reader connected (verified)'
                  : scannerPending
                    ? 'Machine selected — waiting for tag'
                    : 'No active reader'}
              </div>
              <div className="mt-0.5 text-[11px] font-semibold text-slate-500">
                {scannerType ?? '—'} · {deviceConnected?.rfid_device ?? 'Not paired'}
              </div>
            </div>
          </div>

          <div className="rounded-[14px] border border-border bg-card p-3.5">
            <div className="mb-2 text-[13px] font-black text-foreground">Status</div>
            <InfoRow
              label="Link"
              value={
                scannerConnected ? 'Verified' : scannerPending ? 'Pending first tag' : 'Off'
              }
            />
            <InfoRow label="Scanner type" value={scannerType ?? '—'} />
            <InfoRow label="Device" value={deviceConnected?.rfid_device ?? '—'} />
            <InfoRow label="Last select" value={formatDateTime(lastScannerConnectAt)} />
          </div>

          <p className="m-0 text-[11px] font-semibold leading-4 text-muted-foreground">
            RF tuning is not available in the browser. Antenna power, Gen2 session, and inventory
            controls require the native mobile app. This web client only supports HID keyboard-wedge
            tag capture — Connected means a tag was received, not a native SDK Bluetooth link.
          </p>

          {sessionActive ? (
            <AppButton block variant="outline" loading={busy} onClick={() => void onDisconnect()}>
              Clear selection
            </AppButton>
          ) : (
            <AppButton block onClick={() => navigate('/scanner')}>
              Select scanner
            </AppButton>
          )}

          <div className="rounded-[14px] border border-border bg-card p-3.5">
            <div className="mb-1 flex items-center justify-between gap-2">
              <div className="text-[13px] font-black text-foreground">Recent tags</div>
              <div className="text-[11px] font-bold text-muted-foreground">{recentTags.length}</div>
            </div>
            <p className="mb-3 mt-0 text-[11px] font-semibold text-muted-foreground">
              Last {MAX_RECENT} reads while this page is open
            </p>

            {recentTags.length === 0 ? (
              <p className="m-0 py-6 text-center text-xs font-semibold text-muted-foreground">
                No tags yet — connect and pull the trigger
              </p>
            ) : (
              <ul className="m-0 list-none space-y-0 p-0">
                {recentTags.map((row, index) => (
                  <li
                    key={row.key}
                    className="flex items-start justify-between gap-3 py-3"
                    style={{
                      borderTop: index === 0 ? undefined : '1px solid var(--border)',
                    }}
                  >
                    <span className="min-w-0 break-all font-mono text-[11.5px] font-bold text-foreground">
                      {row.tagId}
                    </span>
                    <span className="shrink-0 text-[10px] font-semibold text-muted-foreground">
                      {formatDateTime(row.at)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </PageContainer>
  )
}
